import { DEV } from 'cc/env';
import { _decorator, CCInteger, Gradient, Texture2D, Sprite, UITransform, CCBoolean } from 'cc';
import ShaderManipulator from './ShaderManipulator';
import ITintObject, { Tint } from './TintObject';

const { ccclass, property, executeInEditMode } = _decorator;

@ccclass('TintSprite')
@executeInEditMode
export default class TintSprite extends ShaderManipulator implements ITintObject {
    @property(Sprite)
    private _testSampleColor: Sprite = null;

    @property(CCInteger)
    private _textureWidth: number = 100;

    @property(CCBoolean)
    private _useOriginColor: boolean = false;

    @property(CCBoolean)
    private _premultipliedAlpha: boolean = false;

    @property
    private _grayValueOffset: number = 0;

    @property(Gradient)
    private _tintGradient: Gradient = new Gradient();

    @property
    private _useSampleBound: boolean = false;

    @property
    private _sampleLeftBound: number = 0;

    @property
    private _sampleRightBound: number = 1;

    private _texture: Texture2D = new Texture2D();

    @property({ type: CCInteger, min: 1, max: 10000, displayName: DEV && 'Tint 裁切大小' })
    public get textureWidth(): number {
        return this._textureWidth;
    }
    public set textureWidth(value: number) {
        if (this._textureWidth === value || value < 1 || value > 10000) {
            return;
        }
        this._textureWidth = value;
        this._updateGradientProperty();
    }

    @property({ type: CCBoolean, displayName: DEV && '採用原圖顏色', tooltip: DEV && '是否採用原圖顏色' })
    public get useOriginColor(): boolean {
        return this._useOriginColor;
    }
    public set useOriginColor(value: boolean) {
        if (this._useOriginColor === value) {
            return;
        }
        this._useOriginColor = value;
        Tint.updateUseOriginColorProperty(this.targetPass, value);
    }

    @property({ type: CCBoolean, tooltip: DEV && '是否採用預乘 Alpha' })
    public get premultipliedAlpha(): boolean {
        return this._premultipliedAlpha;
    }
    public set premultipliedAlpha(value: boolean) {
        if (this._premultipliedAlpha === value) {
            return;
        }
        this._premultipliedAlpha = value;
        Tint.updatePremultipliedAlphaProperty(this.targetPass, value);
    }

    @property({ slide: true, range: [0, 1, 0.01], displayName: DEV && '灰階偏移值', tooltip: DEV && '灰階偏移值(0~1)' })
    public get grayValueOffset(): number {
        return this._grayValueOffset;
    }
    public set grayValueOffset(value: number) {
        if (this._grayValueOffset === value) {
            return;
        }
        this._grayValueOffset = value;
        Tint.updateGrayValueOffsetProperty(this.targetPass, value);
    }

    @property({ type: Gradient, displayName: DEV && 'Tint 顏色設定' })
    public get tintGradient(): Gradient {
        return this._tintGradient;
    }
    public set tintGradient(value: Gradient) {
        // if (value.colorKeys.length < 1 || value.colorKeys.length > 8) return;
        if (this._tintGradient === value) {
            return;
        }
        this._tintGradient = value;
        this._updateGradientProperty();
    }

    @property({ type: CCBoolean, displayName: DEV && '啟用範圍採樣', tooltip: DEV && '勾選即可設定顏色採樣範圍' })
    public get useSampleBound(): boolean {
        return this._useSampleBound;
    }
    public set useSampleBound(value: boolean) {
        if (this._useSampleBound === value) {
            return;
        }
        this._useSampleBound = value;
        this._updateGradientProperty();
    }

    @property({
        slide: true,
        range: [0, 1, 0.001],
        displayName: DEV && '採樣左邊界',
        tooltip: DEV && '左邊界大於右邊界時會以左邊界顏色為主',
        visible(this: TintSprite) {
            return this.useSampleBound;
        },
    })
    public get sampleLeftBound(): number {
        return this._sampleLeftBound;
    }
    public set sampleLeftBound(value: number) {
        if (this._sampleLeftBound === value) {
            return;
        }
        this._sampleLeftBound = value;
        this._updateGradientProperty();
    }

    @property({
        slide: true,
        range: [0, 1, 0.001],
        displayName: DEV && '採樣右邊界',
        tooltip: DEV && '左邊界大於右邊界時會以左邊界顏色為主',
        visible(this: TintSprite) {
            return this.useSampleBound;
        },
    })
    public get sampleRightBound(): number {
        return this._sampleRightBound;
    }
    public set sampleRightBound(value: number) {
        if (this._sampleRightBound === value) {
            return;
        }
        this._sampleRightBound = value;
        this._updateGradientProperty();
    }

    @property({ type: Sprite, displayName: DEV && '漸層預覽測試', tooltip: DEV && '圖片大小固定為0或100，用來預覽取樣漸層顏色' })
    public get testSampleColor(): Sprite {
        return this._testSampleColor;
    }
    public set testSampleColor(value: Sprite) {
        if (this._testSampleColor === value) {
            return;
        }
        if (value === null) {
            this._testSampleColor.node.getComponent(UITransform).setContentSize(0, 0);
        }
        this._testSampleColor = value;
        Tint.showSampleColor(this.testSampleColor, this._texture);
    }

    public forceUpdate() {
        this.syncProperty();
    }

    protected syncProperty() {
        this._updateGradientProperty();
        Tint.updateUseOriginColorProperty(this.targetPass, this.useOriginColor);
        Tint.updatePremultipliedAlphaProperty(this.targetPass, this.premultipliedAlpha);
        Tint.updateGrayValueOffsetProperty(this.targetPass, this.grayValueOffset);
    }

    // 將Texture2D傳遞給Shader後更改Material。
    private _updateGradientProperty() {
        if (!this.targetPass) {
            return;
        }
        Tint.assignGradientTexture(this._texture, this.tintGradient, this.textureWidth, this.useSampleBound, this.sampleLeftBound, this.sampleRightBound);
        Tint.showSampleColor(this.testSampleColor, this._texture);
        Tint.updateGradientProperty(this.targetPass, this._texture);
    }
}
